import { faChartPieSimple, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/pro-thin-svg-icons';
import { Chip, Typography } from '@mui/material';
import { FontIcon } from '@resurs-internal/mui';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import { ComplementaryConsolidation } from '../../api/complementaryConsolidation';
import { Coins } from '../../assets/svg/Coins';
import Button from '../../components/Button/Button';
import Group from '../../components/Group/Group';
import Icon from '../../components/Icon/Icon';
import IntegerField from '../../components/IntegerField/IntegerField';
import Spinner from '../../components/Spinner/Spinner';
import TextInputField from '../../components/TextInputField/TextInputField';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useConsolidationTexts } from '../../hooks/useConsolidationTexts';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { Consolidation } from '../../types/ConsolidationTypes';
import {
    CardContainer,
    CardHeader,
    CardHeaderLeft,
    CardTextWrapper,
    SaveButtonWrapper,
} from './ConsolidationLoanCard.styles';

// DK's UI calls this a "FIK-kode"; only the outgoing PATCH payload uses the backend's `kidNumber` key
// (see toComplementaryConsolidation in ConsolidationComplementInfoPageDK.tsx).
export type ComplementaryConsolidationDraft = Omit<ComplementaryConsolidation, 'kidNumber'> & { fikCode?: string };

type Props = {
    consolidation: Consolidation;
    isSaved: boolean;
    onSave: (internalId: string) => Promise<void>;
    onUnsave: (internalId: string) => void;
    finalComplementaryConsolidations: ComplementaryConsolidationDraft[];
    setSaveError: (hasError: boolean) => void;
};

const ConsolidationLoanCardDK = ({ consolidation, isSaved, onSave, onUnsave, setSaveError }: Props) => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { getTitle, getCaption } = useConsolidationTexts();
    const [expanded, setExpanded] = useState(!isSaved);
    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(false);

    const bankNameFieldName = `consolidation_${consolidation.internalId};creditorName`;
    const fikCodeFieldName = `consolidation_${consolidation.internalId};fikCode`;
    const accountNumberFieldName = `consolidation_${consolidation.internalId};accountNumber`;

    const getFieldValue = useAppSelector(selectFieldStateByName);

    const hasFieldError =
        !!getFieldValue(accountNumberFieldName)?.hasError ||
        !!getFieldValue(fikCodeFieldName)?.hasError ||
        (!consolidation.creditorName && !!getFieldValue(bankNameFieldName)?.hasError);

    useEffect(() => {
        setExpanded(!isSaved);
    }, [isSaved]);

    const isPartial = consolidation.consolidationType === 'PARTIAL';

    const handleHeaderClick = async () => {
        if (isSavingRef.current) return;
        if (isSaved) {
            onUnsave(consolidation.internalId);
        } else if (expanded) {
            await handleSave();
        } else {
            setExpanded(true);
        }
    };

    const handleToggleClick = (event: MouseEvent) => {
        event.stopPropagation();
        if (isSavingRef.current) return;
        if (isSaved) {
            onUnsave(consolidation.internalId);
        } else {
            setExpanded((prev) => !prev);
        }
    };

    const handleSave = async () => {
        if (isSavingRef.current) return;
        if (hasFieldError) return;

        const fikCodeField = getFieldValue(fikCodeFieldName);
        const accountNumberField = getFieldValue(accountNumberFieldName);
        const fikCodeValue = String(fikCodeField?.value || '');
        const accountNumberValue = String(accountNumberField?.value || '');
        let isValid = true;

        if (!accountNumberValue || !/^\d{1,14}$/.test(accountNumberValue)) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: accountNumberFieldName,
                    value: accountNumberValue,
                    hasError: true,
                    errorMessage: accountNumberValue
                        ? translate('validation.error.account-number.format')
                        : translate('validation.account-number.required'),
                } as IFieldState),
            );
            isValid = false;
        } else if (accountNumberField?.hasError) {
            isValid = false;
        }

        if (fikCodeValue && !/^\d{15,24}$/.test(fikCodeValue)) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: fikCodeFieldName,
                    value: fikCodeValue,
                    hasError: true,
                    errorMessage: translate('validation.error.fik-code.format'),
                } as IFieldState),
            );
            isValid = false;
        } else if (fikCodeField?.hasError) {
            isValid = false;
        }

        if (!consolidation.creditorName) {
            const bankNameField = getFieldValue(bankNameFieldName);
            const bankNameValue = String(bankNameField?.value || '');
            if (!bankNameValue) {
                dispatch(
                    addOrUpdateFormField({
                        fieldName: bankNameFieldName,
                        value: bankNameValue,
                        hasError: true,
                        errorMessage: translate('validation.bank-name.required'),
                    } as IFieldState),
                );
                isValid = false;
            } else if (bankNameField?.hasError) {
                isValid = false;
            }
        }

        if (!isValid) return;

        try {
            setSaveError(false);
            isSavingRef.current = true;
            setIsSaving(true);
            await onSave(consolidation.internalId);
        } catch {
            setSaveError(true);
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    };

    const accountNumberInput = (
        <IntegerField
            name={accountNumberFieldName}
            label={translate('field.consolidation.account-number')}
            validation={{
                format: '^\\d{1,14}$',
                formatErrorKey: 'validation.error.account-number.format',
            }}
        />
    );
    const fikCodeInput = (
        <IntegerField
            name={fikCodeFieldName}
            label={translate('field.consolidation.fik-code')}
            validation={{
                format: '^\\d{15,24}$',
                formatErrorKey: 'validation.error.fik-code.format',
            }}
        />
    );

    return (
        <CardContainer isSaved={isSaved} data-testid={`loan-card-${consolidation.internalId}`}>
            <CardHeader onClick={handleHeaderClick}>
                <CardHeaderLeft>
                    {isSaved ? (
                        <Icon icon={faCheck} />
                    ) : isPartial ? (
                        <Icon icon={faChartPieSimple} />
                    ) : (
                        <Coins hideBackground={true} />
                    )}
                    <CardTextWrapper>
                        <Typography variant="subtitle1">{getTitle(consolidation)}</Typography>
                        {!isSaved && <Typography variant="caption">{getCaption(consolidation)}</Typography>}
                    </CardTextWrapper>
                </CardHeaderLeft>
                <button
                    type="button"
                    aria-label="Toggle details"
                    onClick={handleToggleClick}
                    style={{ border: 'none', backgroundColor: 'white', cursor: 'pointer' }}>
                    <FontIcon
                        icon={
                            expanded && !isSaved ? (faChevronUp as IconDefinition) : (faChevronDown as IconDefinition)
                        }
                        size="xs"
                        cursor={'pointer'}
                    />
                </button>
            </CardHeader>

            {isPartial && !isSaved && (
                <Chip label={translate('field.partial-consolidation.label')} color="info" sx={{ mt: 1 }} />
            )}

            {expanded && !isSaved && (
                <>
                    {consolidation.creditorName ? (
                        <Group>
                            {accountNumberInput}
                            {fikCodeInput}
                        </Group>
                    ) : (
                        <Group>
                            <TextInputField
                                name={bankNameFieldName}
                                label={translate('field.consolidation.bank-name')}
                            />
                            {accountNumberInput}
                            {fikCodeInput}
                        </Group>
                    )}
                    <SaveButtonWrapper>
                        <Button variant="outlined" onClick={handleSave} fill disabled={isSaving || hasFieldError}>
                            {translate('field.consolidation.save-button')}
                            {isSaving && <Spinner size="sm" buttonLoadingStyles={true} />}
                        </Button>
                    </SaveButtonWrapper>
                </>
            )}
        </CardContainer>
    );
};

export default ConsolidationLoanCardDK;
